import { eventManager } from "@foretagsplatsen/yaem";
import { router } from "@foretagsplatsen/widgetjs";
import { setLanguage as setDateLanguage } from "./helpers/date-util.js";
import { valueOrDefault } from "./helpers/object-util.js";
import hyphenator from "./hyphenator.js";
import polyglot from "./polyglot/polyglot.js";

/**
 * Represents a user session.
 */
export class Session {
	constructor() {
		this._agency = undefined;
		this._company = undefined;
		this._period = undefined;
		this._currentUser = undefined;
		this._country = undefined;

		this.settings = {
			currency: "kr",
			thousandsSep: " ",
			thousandsAbbr: "t",
			millionsAbbr: "m",
			dateFormatLong: "{d} {Month}, {yyyy}",
			dateFormatShort: "{yyyy}-{mm}-{dd}",
		};
	}

	redirectToPeriod(period) {
		router.getRouter().setParameters({ period: period.toString() });
	}

	getPeriod() {
		if (this._period) {
			return this._period;
		}

		if (this._company) {
			return this._company.lastActiveMonth(); // Default period if no set, is the last month in the latest year
		}

		// polymorphic null period
		return {
			format: function () {
				return "period not set";
			},
		};
	}

	setPeriod(newPeriod, triggerEvent) {
		triggerEvent = valueOrDefault(triggerEvent, true);

		if (newPeriod === this._period) {
			return;
		}

		this._period = newPeriod;

		if (triggerEvent) {
			eventManager.at("period").trigger("change", this._period);
		}
	}

	getAccountByNumber(accountNumber) {
		return this.getPeriod().getYear().findAccount(accountNumber);
	}

	getAllAccounts() {
		return this.getPeriod().getYear().getAccounts();
	}

	getFiscalYearId() {
		return this.getPeriod().getYearId();
	}

	onPeriodChange(callback) {
		eventManager.at("period").register("change", callback);
	}

	getCompanyId() {
		if (!this._company) {
			return null;
		}
		return this._company.getLegacyId();
	}

	getCompanyReferentialApiId() {
		return this._company?.getReferentialApiId();
	}

	getCompany() {
		return this._company;
	}

	setCompany(newCompany) {
		// set currency!
		this._company = newCompany;
	}

	getActiveYears() {
		return this.getCompany()?.getActiveYears();
	}

	getAgency() {
		return this._agency;
	}

	getAgencyId() {
		const agencyId = this._agency
			? this._agency.getId()
			: this._company?.getAgencyId();

		return agencyId || null;
	}

	getAgencyReferentialApiId() {
		return this._agency?.getReferentialApiId();
	}

	setAgency(newAgency) {
		this._agency = newAgency;
	}

	getCountry() {
		return this._country;
	}

	getCountryId() {
		if (!this._country) {
			return null;
		}
		return this._country.getId();
	}

	getCountryReferentialApiId() {
		if (!this._country) {
			return null;
		}
		return this._country.getReferentialApiId();
	}

	getCountryLegacyId() {
		return this._country?.getLegacyId();
	}

	getSupportInfo() {
		return this._country && this._country.getSupportInfo();
	}

	getCountrySettings() {
		return this._country && this._country.getCountrySettings();
	}

	getVatPaymentSettings() {
		return this.getCountrySettings()
			?.getVatSettings()
			?.getVatPaymentSettings();
	}

	setCountry(newCountry) {
		this._country = newCountry;
	}

	setInternationalizationService(internationalizationService) {
		this._i18nService = internationalizationService;
	}

	async setLanguage(lang) {
		await this._i18nService.ensureTranslations(lang);

		polyglot.setLanguage(lang);
		setDateLanguage(lang);
		this._currentUser?.setLanguage(lang);
		hyphenator.setLanguage(lang);
		eventManager.at("app").trigger("updateLanguage");
	}

	getLanguage() {
		return polyglot.getLanguage();
	}

	getCurrentUser() {
		return this._currentUser;
	}

	setCurrentUser(user) {
		this._currentUser = user;
	}

	getCurrentChartOfAccountsId() {
		return this.getPeriod().getYear().getChartOfAccountsId();
	}

	getDimensionTree() {
		return this.getPeriod().getDimensionTree();
	}

	hasSupportInfo() {
		const supportInfo = this.getSupportInfo();

		return supportInfo && !supportInfo.isEmpty();
	}

	getAntiForgeryToken() {
		return this._antiForgeryToken;
	}

	setAntiForgeryToken(antiForgeryToken) {
		this._antiForgeryToken = antiForgeryToken;
	}
}

export default new Session();
